import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer from './slices/authSlice';
import dataReducer from './slices/dataSlice';
import integrationReducer from './slices/integrationSlice';

import recordingReducer from './slices/recordingSlice';
import sectionRecordingReducer from './slices/sectionRecordingSlice';

// Configure persistence for auth slice - ONLY persist non-sensitive data
const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: [
    'user',
    'isAuthenticated',
    'integrationToken', // Persist integration token
  ], // Persist user data, auth status, and integration token
  blacklist: [
    'accessToken',
    'refreshToken',
    'tokenExpiry',
    'loading',
    'sessionValidation',
  ], // Don't persist sensitive tokens, loading state, or session validation status
};

// Configure persistence for integration slice
const integrationPersistConfig = {
  key: 'integration',
  storage,
  whitelist: ['partner', 'endpoints', 'dataFetched'], // Persist integration partner and endpoint data
  blacklist: ['loading', 'error'], // Don't persist loading and error states
};

// Configure persistence for data slice - persist user locations and selected location
const dataPersistConfig = {
  key: 'data',
  storage,
  whitelist: ['userLocations', 'selectedLocation'], // Persist user locations and selected location
  blacklist: ['appointments', 'cache'], // Don't persist appointments and cache
};

// Combine reducers
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  data: persistReducer(dataPersistConfig, dataReducer), // Add persistence to data slice
  integration: persistReducer(integrationPersistConfig, integrationReducer),
  recording: recordingReducer, // No persistence for recording slice
  sectionRecording: sectionRecordingReducer, // No persistence for section recording slice
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
